import React from 'react';
import { Box, HStack, VStack, Text, Heading, Flex, useColorMode, Tag } from '@chakra-ui/react';
import { format } from 'date-fns';

import { CalendarPage } from '../../ui';
import { LocationPinIcon, ClockIcon } from '../../icons';
import { useCalendarSettings, getGMTOffset } from '../../../lib';

import type { IEvent } from '../../../lib';

interface IBasicInfoSectionProps {
	event: IEvent;
	isSameDayEvent: boolean;
	monthFormat: string;
	timeFormat: string;
}
export const BasicInfoSection: React.FunctionComponent<IBasicInfoSectionProps> = ({
	event,
	isSameDayEvent,
	monthFormat,
	timeFormat,
}) => {
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const bg = settings.styles[colorMode]?.eventModalBannerColor || '#4cc2e6';
	const { categoryVariant } = settings;
	const gmtStart = getGMTOffset(event.start);

	return (
		<Box as='section' bg={bg} p={6} color='white' position='relative'>
			<HStack alignItems='flex-start'>
				<HStack className='event-dates' gap={0} alignItems='center' pr={6} pt={0}>
					<CalendarPage
						bg='white'
						color={bg}
						day={format(event.start, 'dd')}
						month={format(event.start, 'MMM')}
					/>
					{!isSameDayEvent && (
						<>
							<Text fontWeight='900' className='margin-0'>
								-
							</Text>
							<CalendarPage
								bg='white'
								color={bg}
								day={format(event.end, 'dd')}
								month={format(event.end, 'MMM')}
							/>
						</>
					)}
				</HStack>
				<VStack alignItems='flex-start'>
					<HStack spacing={4}>
						{Array.isArray(event.eventType) &&
							event.eventType.length > 0 &&
							event.eventType.map((eventType) => (
								<Tag
									size='md'
									key={eventType}
									variant={categoryVariant}
									background={settings?.eventCategoryColors[eventType.trim()]}
									marginInlineStart='4px !important'
								>
									{eventType}
								</Tag>
							))}
					</HStack>
					<Heading fontSize='30px' fontWeight='900' color='white'>
						{event.title}
					</Heading>
					<Flex m={0} fontSize='14px' fontWeight='500'>
						<Flex as='span' mr={1} pt='2px'>
							<ClockIcon size={12} />
						</Flex>
						{format(event.start, isSameDayEvent ? timeFormat : `${monthFormat} ${timeFormat}`)} -{' '}
						{format(event.end, isSameDayEvent ? timeFormat : `${monthFormat} ${timeFormat}`)}
						{` (${gmtStart})`}
					</Flex>
					{event.venue && (
						<Flex m={0} fontSize='14px' fontWeight='500'>
							<Flex as='span' mr={1} pt='2px'>
								<LocationPinIcon size={12} />
							</Flex>
							{event.venue}
						</Flex>
					)}

					<HStack spacing={4}>
						{Array.isArray(event.tags) &&
							event.tags.length > 0 &&
							event.tags.map((tag) => (
								<Tag
									size='md'
									key={tag}
									variant='solid'
									colorScheme='gray'
									borderRadius='xs'
									marginInlineStart='4px !important'
								>
									{tag}
								</Tag>
							))}
					</HStack>
				</VStack>
			</HStack>
		</Box>
	);
};
